import type {
  HasAccessFunction,
  MenuLink,
  NavCategory,
  NonMenuLink,
  RootGroup,
  RootLink,
} from '../@types/nav-object';
import { checkHasAccess } from './check-route-access';

export async function buildPathsWithAccess(
  navCategories: NavCategory[],
): Promise<Map<string, Set<string>>> {
  const requestedPaths: Promise<[string, string] | null>[] = [];

  const getChildrenAccessPaths = ({
    routes,
    parentPath,
    parentHasAccess,
    parentKey,
  }: {
    routes: (RootLink | RootGroup | MenuLink | NonMenuLink)[];
    parentPath: string;
    parentHasAccess?: HasAccessFunction;
    parentKey: string;
  }) => {
    routes.forEach(async (route, index) => {
      const key = `${parentKey}.${index}`;
      const path = route.index ? parentPath : `${parentPath}/${route.path}`;

      if (Array.isArray(route.children)) {
        getChildrenAccessPaths({
          routes: route.children,
          parentPath: path,
          parentHasAccess: route.hasAccess,
          parentKey: key,
        });
      }

      const routeFunc = async () => {
        const parentAccess = parentHasAccess
          ? await checkHasAccess(parentHasAccess, false)
          : true;
        const routeHasAccess = route.hasAccess
          ? await checkHasAccess(route.hasAccess, false)
          : true;

        return parentAccess && routeHasAccess
          ? ([path, key] as [string, string])
          : null;
      };
      requestedPaths.push(routeFunc());
    });
  };

  navCategories.forEach((category, index) => {
    if (Array.isArray(category.children)) {
      getChildrenAccessPaths({
        routes: category.children,
        parentPath: '',
        parentHasAccess: category.hasAccess,
        parentKey: `${index}`,
      });
    }
  });

  const resolvedPromises = await Promise.all(requestedPaths);
  return resolvedPromises.reduce((acc, response) => {
    if (response) {
      const [path, key] = response;
      const existingKeys = acc.get(path);

      if (existingKeys) {
        existingKeys.add(key);
      } else {
        acc.set(path, new Set([key]));
      }
    }
    return acc;
  }, new Map<string, Set<string>>());
}
